import { ChainId, JSBI, Percent, Token, WETH } from '@pancakeswap-libs/sdk'

// LiberaDex's own Router, deployed by this repo's hardhat/ project to the real,
// public BSC Testnet - NOT PancakeSwap's mainnet router.
export const ROUTER_ADDRESS = '0xb618e7732a55066E4dcDD24406FA13446C52A6e5'

// LiberaDex's own LBC token, deployed alongside the router above. Not featured as a
// default/suggested/pinned token for now (still swappable if pasted directly) -
// this is meant to read as a general-purpose swap tool, not an LBC promotion.
export const LBC = new Token(ChainId.BSCTESTNET, '0x7EcB81D2Eebb42CC872fCe8E1e7C672EE601683d', 18, 'LBC', 'LiberaCoin')

// a list of tokens by chain
type ChainTokenList = {
  readonly [chainId in ChainId]: Token[]
}

export const DAI = new Token(ChainId.MAINNET, '0x1af3f329e8be154074d8769d1ffa4ee058b1dbc3', 18, 'DAI', 'Dai Stablecoin')
export const BUSD = new Token(ChainId.MAINNET, '0xe9e7cea3dedca5984780bafc599bd69add087d56', 18, 'BUSD', 'Binance USD')
export const USDT = new Token(ChainId.MAINNET, '0x55d398326f99059ff775485246999027b3197955', 18, 'USDT', 'Tether USD')
export const UST = new Token(
  ChainId.MAINNET,
  '0x23396cf899ca06c4472205fc903bdb4de249d6fc',
  18,
  'UST',
  'Wrapped UST Token'
)
export const ETH = new Token(
  ChainId.MAINNET,
  '0x2170ed0880ac9a755fd29b2688956bd959f933f8',
  18,
  'ETH',
  'Binance-Peg Ethereum Token'
)

// Mock tokens deployed alongside LBC on the real, public BSC Testnet
// (hardhat/scripts/deploy-tokens.ts, see hardhat/deployed.tokens.json), each seeded
// with real WBNB liquidity so they're actually swappable here - not the real
// mainnet BUSD/USDT/ETH/BTCB, which are separate tokens entirely.
export const BUSD_TESTNET = new Token(ChainId.BSCTESTNET, '0x13b2a48DA79A79D58e8D88271eAE02803249cAE3', 18, 'BUSD', 'BUSD Token')
export const USDT_TESTNET = new Token(ChainId.BSCTESTNET, '0xb2C036DF39B7B5cf3Ae9140FD25821B09dD97a85', 18, 'USDT', 'Tether USD')
export const ETH_TESTNET = new Token(ChainId.BSCTESTNET, '0x5886cb37F2daff0f47D43d6A7fCE7bBa2e776370', 18, 'ETH', 'Binance-Peg Ethereum Token')
export const BTCB_TESTNET = new Token(ChainId.BSCTESTNET, '0xf80A507cca5D1eB51EB73B53f4366D1EF4A985D6', 18, 'BTCB', 'Binance-Peg BTCB Token')

const WETH_ONLY: ChainTokenList = {
  [ChainId.MAINNET]: [WETH[ChainId.MAINNET]],
  [ChainId.BSCTESTNET]: [WETH[ChainId.BSCTESTNET]],
}

// used to construct intermediary pairs for trading
export const BASES_TO_CHECK_TRADES_AGAINST: ChainTokenList = {
  ...WETH_ONLY,
  [ChainId.MAINNET]: [...WETH_ONLY[ChainId.MAINNET], DAI, BUSD, USDT, UST, ETH],
  [ChainId.BSCTESTNET]: [...WETH_ONLY[ChainId.BSCTESTNET], BUSD_TESTNET, USDT_TESTNET, ETH_TESTNET, BTCB_TESTNET],
}

/**
 * Some tokens can only be swapped via certain pairs, so we override the list of bases that are considered for these
 * tokens.
 */
export const CUSTOM_BASES: { [chainId in ChainId]?: { [tokenAddress: string]: Token[] } } = {
  [ChainId.MAINNET]: {},
}

// used for display in the default list when adding liquidity
export const SUGGESTED_BASES: ChainTokenList = {
  ...WETH_ONLY,
  [ChainId.MAINNET]: [...WETH_ONLY[ChainId.MAINNET], DAI, BUSD, USDT],
  [ChainId.BSCTESTNET]: [...WETH_ONLY[ChainId.BSCTESTNET], BUSD_TESTNET, USDT_TESTNET, ETH_TESTNET, BTCB_TESTNET],
}

// used to construct the list of all pairs we consider by default in the frontend
export const BASES_TO_TRACK_LIQUIDITY_FOR: ChainTokenList = {
  ...WETH_ONLY,
  [ChainId.MAINNET]: [...WETH_ONLY[ChainId.MAINNET], DAI, BUSD, USDT],
  [ChainId.BSCTESTNET]: [...WETH_ONLY[ChainId.BSCTESTNET], BUSD_TESTNET, USDT_TESTNET, ETH_TESTNET, BTCB_TESTNET],
}

export const PINNED_PAIRS: { readonly [chainId in ChainId]?: [Token, Token][] } = {
  [ChainId.MAINNET]: [
    [BUSD, USDT],
    [DAI, USDT],
  ],
  [ChainId.BSCTESTNET]: [[BUSD_TESTNET, USDT_TESTNET]],
}

export const NetworkContextName = 'NETWORK'

// default allowed slippage, in bips
export const INITIAL_ALLOWED_SLIPPAGE = 80
// 20 minutes, denominated in seconds
export const DEFAULT_DEADLINE_FROM_NOW = 60 * 20

// one basis point
export const ONE_BIPS = new Percent(JSBI.BigInt(1), JSBI.BigInt(10000))
export const BIPS_BASE = JSBI.BigInt(10000)
// used for warning states
export const ALLOWED_PRICE_IMPACT_LOW: Percent = new Percent(JSBI.BigInt(100), BIPS_BASE) // 1%
export const ALLOWED_PRICE_IMPACT_MEDIUM: Percent = new Percent(JSBI.BigInt(300), BIPS_BASE) // 3%
export const ALLOWED_PRICE_IMPACT_HIGH: Percent = new Percent(JSBI.BigInt(500), BIPS_BASE) // 5%
// if the price slippage exceeds this number, force the user to type 'confirm' to execute
export const PRICE_IMPACT_WITHOUT_FEE_CONFIRM_MIN: Percent = new Percent(JSBI.BigInt(1000), BIPS_BASE) // 10%
// for non expert mode disable swaps above this
export const BLOCKED_PRICE_IMPACT_NON_EXPERT: Percent = new Percent(JSBI.BigInt(1500), BIPS_BASE) // 15%

// used to ensure the user doesn't send so much ETH so they end up with <.01
export const MIN_ETH: JSBI = JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(16)) // .01 ETH
